import * as XLSX from 'xlsx';
import {
  compactExcelLabel,
  isPlaceholderExcelValue,
  normalizeExcelLabel,
  resolveHeaderAliasKey,
  resolveItemAliasKey,
  resolveUnsupportedQuotationItemLabel,
  type RfqExcelHeaderAliasKey,
  type RfqExcelItemAliasKey,
} from './rfq-excel-aliases';
import type { RfqExcelItem } from './rfq-excel.types';
import { VALID_PRIORITIES, VALID_QUALITIES, VALID_UOMS } from './rfq-excel.types';

const MAX_SCAN_ROWS = 80;
const MAX_SCAN_COLS = 40;
const SKIP_SHEET_KEYS = new Set([
  'instructions',
  'referencedata',
  'reference',
  'fieldmap',
  'fieldmapping',
  'mapping',
]);

export type HeaderLayoutKind =
  | 'field_value'
  | 'section_field_data'
  | 'horizontal'
  | 'label_colon_value'
  | 'label_row_value_row'
  | 'none';

export interface FlexibleHeaderExtraction {
  fields: Record<string, string>;
  confidence: number;
  layout: HeaderLayoutKind;
  unmatchedLabels: string[];
  matchCount: number;
}

export interface FlexibleItemsExtraction {
  items: RfqExcelItem[];
  warnings: string[];
  confidence: number;
  headerRowIndex: number;
  columnMap: Partial<Record<RfqExcelItemAliasKey, number>>;
  matchCount: number;
  /** Offer/quotation columns detected but not mapped into RFQ item model. */
  unsupportedQuotationFields: string[];
}

function sheetRows(sheet: XLSX.WorkSheet): string[][] {
  const rows = XLSX.utils.sheet_to_json<(string | number | boolean | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
  });
  return rows.slice(0, MAX_SCAN_ROWS).map((row) => {
    if (!Array.isArray(row)) return [];
    return row.slice(0, MAX_SCAN_COLS).map((c) => String(c ?? '').trim());
  });
}

function cellText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function stripRequiredMarker(label: string): string {
  return label.replace(/\s*\*\s*$/, '').trim();
}

function normalizeUom(value: string): string {
  const map: Record<string, string> = {
    pcs: 'Pcs',
    pc: 'Pcs',
    kgs: 'Kgs',
    kg: 'Kgs',
    ltrs: 'Ltrs',
    ltr: 'Ltrs',
    l: 'Ltrs',
    mtrs: 'Mtrs',
    mtr: 'Mtrs',
    m: 'Mtrs',
    sets: 'Sets',
    set: 'Sets',
    box: 'Box',
    boxes: 'Box',
  };
  return map[normalizeExcelLabel(value).replace(/\s+/g, '')] ?? value;
}

/** Labels that belong to item tables — not "failed header" unmatched noise. */
function isItemColumnLabel(label: string): boolean {
  return resolveItemAliasKey(label) != null;
}

function pushUnmatchedLabel(target: string[], label: string): void {
  const trimmed = label.trim();
  if (!trimmed || trimmed.length < 2 || trimmed.length >= 80) return;
  if (isItemColumnLabel(trimmed)) return;
  if (resolveUnsupportedQuotationItemLabel(trimmed)) return;
  if (!target.includes(trimmed)) target.push(trimmed);
}

function normalizeQuality(raw: string): string {
  if (isPlaceholderExcelValue(raw)) return '';
  const value = normalizeExcelLabel(raw);
  if (!value) return '';
  const match = VALID_QUALITIES.find(
    (q) =>
      normalizeExcelLabel(q) === value ||
      compactExcelLabel(q) === compactExcelLabel(raw)
  );
  if (match) return match;
  if (value === 'copy parts' || value === 'copy part' || compactExcelLabel(raw) === 'copyparts') {
    return 'Copy Parts';
  }
  return raw.trim();
}

function meaningfulCellValue(raw: string): string {
  if (isPlaceholderExcelValue(raw)) return '';
  return String(raw ?? '').trim();
}

function createEmptyItem(): RfqExcelItem {
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    impaNo: '',
    itemDescription: '',
    drawingNumber: '',
    partNo: '',
    altPartNo: '',
    modelSerial: '',
    positionNo: '',
    dimensions: '',
    requiredQuantity: '',
    uom: '',
    quality: '',
    generalRemark: '',
    deliveryPriority: 'normal',
    expectedDeliveryDate: '',
  };
}

function parseItemDate(value: string): string {
  if (!value?.trim()) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.trim();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function isSkipSheetName(name: string): boolean {
  return SKIP_SHEET_KEYS.has(compactExcelLabel(name));
}

/** Sheets that belong to RFQ list/report exports — not Create-RFQ import templates. */
const LIST_EXPORT_SHEET_KEYS = new Set(['vessels', 'rfqs', 'rfqlist', 'summary', 'errors']);

const LIST_EXPORT_COLUMN_MARKERS = [
  'rfqid',
  'vesselid',
  'sendertype',
  'organizationid',
  'itemcount',
  'createdat',
  'updatedat',
];

/**
 * Cheap probe: does any sheet contain a Description + Quantity + UOM header
 * with at least one plausible data row? Uses existing item aliases (not full extract).
 * Does NOT treat Item_Count / RFQ_ID report columns as an item table.
 */
function workbookHasCredibleRfqItemTable(workbook: XLSX.WorkBook): boolean {
  for (const sheetName of workbook.SheetNames) {
    if (isSkipSheetName(sheetName)) continue;
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    const detected = findItemTableHeader(sheet);
    if (!detected) continue;

    const rows = sheetRows(sheet);
    const { headerRowIndex, columnMap } = detected;
    const descIdx = columnMap.itemDescription;
    const qtyIdx = columnMap.requiredQuantity;
    const uomIdx = columnMap.uom;
    if (descIdx == null || qtyIdx == null || uomIdx == null) continue;

    for (let r = headerRowIndex + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const description = cellText(row[descIdx]);
      const quantity = cellText(row[qtyIdx]);
      const uom = cellText(row[uomIdx]);
      if (!description && !quantity && !uom) continue;

      // Skip header-echo / label rows
      if (
        resolveItemAliasKey(description) === 'itemDescription' ||
        (compactExcelLabel(description) === 'description' &&
          compactExcelLabel(quantity) === 'quantity')
      ) {
        continue;
      }

      if (description && quantity && uom) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Detect RFQ list / analytics workbooks that are not Create-RFQ import templates.
 * Example sheets: Vessels, RFQs, Summary with columns RFQ_ID, Item_Count, …
 *
 * Softened: if a credible Description/Quantity/UOM item table also exists,
 * do not hard-reject — let the flexible parser run.
 */
function detectUnsupportedRfqListWorkbook(workbook: XLSX.WorkBook): string | null {
  const compactNames = workbook.SheetNames.map((n) => compactExcelLabel(n));
  const listSheetHits = compactNames.filter((n) => LIST_EXPORT_SHEET_KEYS.has(n)).length;

  let markerHits = 0;
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;
    const rows = sheetRows(sheet);
    const headerCells = (rows[0] ?? []).map((c) => compactExcelLabel(c)).filter(Boolean);
    for (const cell of headerCells) {
      if (LIST_EXPORT_COLUMN_MARKERS.includes(cell)) markerHits += 1;
    }
  }

  const looksLikeListExport =
    (listSheetHits >= 2 && markerHits >= 2) ||
    markerHits >= 4 ||
    (compactNames.includes('rfqs') &&
      compactNames.includes('vessels') &&
      compactNames.includes('summary'));

  if (!looksLikeListExport) return null;

  // CASE B: report-like sheet names/markers but a real item table is present
  if (workbookHasCredibleRfqItemTable(workbook)) {
    return null;
  }

  return (
    'This Excel file looks like an RFQ list/report export (sheets such as "Vessels", "RFQs", "Summary" ' +
    'with columns like RFQ_ID / Item_Count), not a Create RFQ import workbook. ' +
    'Download the Create RFQ template (RFQ_Header + RFQ_Items tables), fill those sheets, then re-upload.'
  );
}

function isOfficialHeaderSheetName(name: string): boolean {
  const c = compactExcelLabel(name);
  return c === 'rfqheader' || c === 'header';
}

function isOfficialItemsSheetName(name: string): boolean {
  const c = compactExcelLabel(name);
  return c === 'rfqitems' || c === 'items' || c === 'lineitems' || c === 'lineitem';
}

/**
 * STRUCTURE A: Field | Value (vertical pairs in first two columns)
 * Also catches official RFQ_Header layout.
 */
function extractFieldValueLayout(rows: string[][]): FlexibleHeaderExtraction {
  const fields: Record<string, string> = {};
  const unmatchedLabels: string[] = [];
  let matchCount = 0;

  for (const row of rows) {
    if (row.length < 2) continue;
    const label = stripRequiredMarker(row[0] ?? '');
    const value = meaningfulCellValue(cellText(row[1]));
    if (!label || !value) continue;

    const labelNorm = normalizeExcelLabel(label);
    if (labelNorm === 'field' || labelNorm.includes('rfq header')) continue;
    if (label.startsWith('←')) continue;

    const key = resolveHeaderAliasKey(label);
    if (!key) {
      // Ignore obvious non-field noise and item-table column titles
      pushUnmatchedLabel(unmatchedLabels, label);
      continue;
    }
    if (!fields[key]) {
      fields[key] = value;
      matchCount += 1;
    }
  }

  return {
    fields,
    confidence: matchCount >= 2 ? Math.min(1, 0.35 + matchCount * 0.08) : matchCount * 0.15,
    layout: matchCount > 0 ? 'field_value' : 'none',
    unmatchedLabels,
    matchCount,
  };
}

/**
 * STRUCTURE A2: Section | Field | Data (offer "Fields & Data" workbooks)
 */
function extractSectionFieldDataLayout(rows: string[][]): FlexibleHeaderExtraction {
  const fields: Record<string, string> = {};
  const unmatchedLabels: string[] = [];
  let matchCount = 0;
  let fieldCol = -1;
  let dataCol = -1;
  let startRow = 0;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const row = rows[i] ?? [];
    const norms = row.map((c) => normalizeExcelLabel(c));
    const fi = norms.findIndex((c) => c === 'field');
    const di = norms.findIndex((c) => c === 'data' || c === 'value');
    if (fi >= 0 && di >= 0 && fi !== di) {
      fieldCol = fi;
      dataCol = di;
      startRow = i + 1;
      break;
    }
  }

  // Fallback: many offer exports use fixed Section | Field | Data without needing the header row
  if (fieldCol < 0 || dataCol < 0) {
    let aliasHits = 0;
    for (let i = 0; i < Math.min(rows.length, 30); i++) {
      const row = rows[i] ?? [];
      if (row.length < 3) continue;
      if (resolveHeaderAliasKey(stripRequiredMarker(row[1] ?? ''))) aliasHits += 1;
    }
    if (aliasHits >= 3) {
      fieldCol = 1;
      dataCol = 2;
      startRow = 0;
    } else {
      return {
        fields: {},
        confidence: 0,
        layout: 'none',
        unmatchedLabels: [],
        matchCount: 0,
      };
    }
  }

  for (let i = startRow; i < rows.length; i++) {
    const row = rows[i] ?? [];
    const label = stripRequiredMarker(row[fieldCol] ?? '');
    const value = meaningfulCellValue(cellText(row[dataCol]));
    if (!label || !value) continue;
    const labelNorm = normalizeExcelLabel(label);
    if (labelNorm === 'field' || labelNorm === 'section') continue;

    const key = resolveHeaderAliasKey(label);
    if (!key) {
      pushUnmatchedLabel(unmatchedLabels, label);
      continue;
    }
    if (!fields[key]) {
      fields[key] = value;
      matchCount += 1;
    }
  }

  return {
    fields,
    confidence: matchCount >= 2 ? Math.min(1, 0.4 + matchCount * 0.08) : matchCount * 0.15,
    layout: matchCount > 0 ? 'section_field_data' : 'none',
    unmatchedLabels,
    matchCount,
  };
}

/**
 * STRUCTURE B / D: header labels in one row, values in the next (or same table).
 */
function extractHorizontalLayout(rows: string[][]): FlexibleHeaderExtraction {
  let best: FlexibleHeaderExtraction = {
    fields: {},
    confidence: 0,
    layout: 'none',
    unmatchedLabels: [],
    matchCount: 0,
  };

  for (let r = 0; r < rows.length - 1; r++) {
    const labelRow = rows[r];
    const valueRow = rows[r + 1];
    if (!labelRow?.length || !valueRow?.length) continue;

    const colKeys: Array<RfqExcelHeaderAliasKey | null> = [];
    let hits = 0;
    const unmatched: string[] = [];

    for (let c = 0; c < labelRow.length; c++) {
      const label = stripRequiredMarker(labelRow[c] ?? '');
      if (!label) {
        colKeys.push(null);
        continue;
      }
      const key = resolveHeaderAliasKey(label);
      colKeys.push(key);
      if (key) hits += 1;
      else pushUnmatchedLabel(unmatched, label);
    }

    // Need at least 2 header aliases in the label row
    if (hits < 2) continue;

    // Value row should not itself look like another header row of aliases
    let valueLooksLikeHeaders = 0;
    for (const cell of valueRow) {
      if (cell && resolveHeaderAliasKey(cell)) valueLooksLikeHeaders += 1;
    }
    if (valueLooksLikeHeaders >= hits) continue;

    const fields: Record<string, string> = {};
    let filled = 0;
    for (let c = 0; c < colKeys.length; c++) {
      const key = colKeys[c];
      const value = meaningfulCellValue(cellText(valueRow[c]));
      if (!key || !value) continue;
      if (!fields[key]) {
        fields[key] = value;
        filled += 1;
      }
    }

    const confidence = Math.min(1, 0.4 + hits * 0.07 + filled * 0.05);
    if (filled > best.matchCount || (filled === best.matchCount && confidence > best.confidence)) {
      best = {
        fields,
        confidence,
        layout: 'horizontal',
        unmatchedLabels: unmatched,
        matchCount: filled,
      };
    }
  }

  return best;
}

/**
 * STRUCTURE C: "Label: Value" or "Label - Value" in a single cell (or label in col0).
 */
function extractColonValueLayout(rows: string[][]): FlexibleHeaderExtraction {
  const fields: Record<string, string> = {};
  const unmatchedLabels: string[] = [];
  let matchCount = 0;

  for (const row of rows) {
    for (const cell of row) {
      if (!cell || !cell.includes(':')) continue;
      const idx = cell.indexOf(':');
      const label = stripRequiredMarker(cell.slice(0, idx));
      const value = meaningfulCellValue(cellText(cell.slice(idx + 1)));
      if (!label || !value) continue;
      const key = resolveHeaderAliasKey(label);
      if (!key) {
        pushUnmatchedLabel(unmatchedLabels, label);
        continue;
      }
      if (!fields[key]) {
        fields[key] = value;
        matchCount += 1;
      }
    }
  }

  return {
    fields,
    confidence: matchCount >= 2 ? Math.min(1, 0.3 + matchCount * 0.08) : matchCount * 0.12,
    layout: matchCount > 0 ? 'label_colon_value' : 'none',
    unmatchedLabels,
    matchCount,
  };
}

export function extractFlexibleHeader(sheet: XLSX.WorkSheet): FlexibleHeaderExtraction {
  const rows = sheetRows(sheet);
  const candidates = [
    extractSectionFieldDataLayout(rows),
    extractFieldValueLayout(rows),
    extractHorizontalLayout(rows),
    extractColonValueLayout(rows),
  ];

  candidates.sort((a, b) => {
    if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
    return b.confidence - a.confidence;
  });

  return candidates[0] ?? {
    fields: {},
    confidence: 0,
    layout: 'none',
    unmatchedLabels: [],
    matchCount: 0,
  };
}

function mapItemHeaderRow(
  row: string[]
): { columnMap: Partial<Record<RfqExcelItemAliasKey, number>>; matchCount: number } {
  const columnMap: Partial<Record<RfqExcelItemAliasKey, number>> = {};
  let matchCount = 0;
  for (let c = 0; c < row.length; c++) {
    const label = stripRequiredMarker(row[c] ?? '');
    if (!label) continue;
    const key = resolveItemAliasKey(label);
    if (!key) continue;
    if (columnMap[key] == null) {
      columnMap[key] = c;
      matchCount += 1;
    }
  }
  return { columnMap, matchCount };
}

function isConfidentItemTable(
  columnMap: Partial<Record<RfqExcelItemAliasKey, number>>
): boolean {
  return (
    columnMap.itemDescription != null &&
    columnMap.requiredQuantity != null &&
    columnMap.uom != null
  );
}

export function findItemTableHeader(
  sheet: XLSX.WorkSheet
): {
  headerRowIndex: number;
  columnMap: Partial<Record<RfqExcelItemAliasKey, number>>;
  matchCount: number;
} | null {
  const rows = sheetRows(sheet);
  let best: {
    headerRowIndex: number;
    columnMap: Partial<Record<RfqExcelItemAliasKey, number>>;
    matchCount: number;
  } | null = null;

  for (let i = 0; i < rows.length; i++) {
    const { columnMap, matchCount } = mapItemHeaderRow(rows[i] ?? []);
    if (!isConfidentItemTable(columnMap)) continue;
    if (!best || matchCount > best.matchCount) {
      best = { headerRowIndex: i, columnMap, matchCount };
    }
  }
  return best;
}

export function extractFlexibleItems(sheet: XLSX.WorkSheet): FlexibleItemsExtraction {
  const detected = findItemTableHeader(sheet);
  if (!detected) {
    return {
      items: [],
      warnings: [],
      confidence: 0,
      headerRowIndex: -1,
      columnMap: {},
      matchCount: 0,
      unsupportedQuotationFields: [],
    };
  }

  const rows = sheetRows(sheet);
  const { headerRowIndex, columnMap, matchCount } = detected;
  const warnings: string[] = [];
  const items: RfqExcelItem[] = [];
  const unsupportedQuotationFields: string[] = [];

  const headerRow = rows[headerRowIndex] ?? [];
  for (const cell of headerRow) {
    const label = stripRequiredMarker(cell ?? '');
    if (!label) continue;
    const unsupported = resolveUnsupportedQuotationItemLabel(label);
    if (unsupported && !unsupportedQuotationFields.includes(label.trim())) {
      unsupportedQuotationFields.push(label.trim());
    }
  }

  const col = (key: RfqExcelItemAliasKey): number => columnMap[key] ?? -1;
  const get = (row: string[], key: RfqExcelItemAliasKey): string => {
    const idx = col(key);
    if (idx < 0) return '';
    return meaningfulCellValue(cellText(row[idx]));
  };

  for (let r = headerRowIndex + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const description = get(row, 'itemDescription');
    const quantity = get(row, 'requiredQuantity');
    const uomRaw = get(row, 'uom');
    const uom = normalizeUom(uomRaw);

    if (!description && !quantity && !uomRaw) continue;

    // Skip template sample / header-echo rows
    if (compactExcelLabel(description) === 'pistonringset' && quantity === '5') continue;
    if (
      resolveItemAliasKey(description) === 'itemDescription' ||
      (compactExcelLabel(description) === 'description' &&
        compactExcelLabel(quantity) === 'quantity')
    ) {
      continue;
    }

    const displayRow = r + 1;
    if (!description || !quantity || !uomRaw) {
      warnings.push(
        `Item row ${displayRow}: missing Description, Quantity, or UOM — skipped`
      );
      continue;
    }

    if (!VALID_UOMS.includes(uom)) {
      warnings.push(`Item row ${displayRow}: UOM "${uomRaw}" not recognized — left as entered`);
    }

    const priorityRaw = normalizeExcelLabel(get(row, 'deliveryPriority'));
    const priority = VALID_PRIORITIES.includes(priorityRaw) ? priorityRaw : 'normal';
    if (priorityRaw && !VALID_PRIORITIES.includes(priorityRaw)) {
      warnings.push(
        `Item row ${displayRow}: Priority "${priorityRaw}" invalid — defaulted to normal`
      );
    }

    const qIdx = col('quality');
    const qualityRaw = qIdx >= 0 ? cellText(row[qIdx]) : '';
    const quality = normalizeQuality(qualityRaw);
    if (
      qualityRaw &&
      !isPlaceholderExcelValue(qualityRaw) &&
      quality &&
      !VALID_QUALITIES.includes(quality)
    ) {
      warnings.push(
        `Item row ${displayRow}: Quality "${qualityRaw}" not recognized — left as entered`
      );
    }

    items.push({
      ...createEmptyItem(),
      itemDescription: description,
      requiredQuantity: quantity,
      uom: VALID_UOMS.includes(uom) ? uom : uomRaw,
      impaNo: get(row, 'impaNo'),
      drawingNumber: get(row, 'drawingNumber'),
      partNo: get(row, 'partNo'),
      altPartNo: get(row, 'altPartNo'),
      positionNo: get(row, 'positionNo'),
      modelSerial: get(row, 'modelSerial'),
      dimensions: get(row, 'dimensions'),
      quality: quality || '',
      deliveryPriority: priority,
      expectedDeliveryDate: parseItemDate(get(row, 'expectedDeliveryDate')),
      generalRemark: get(row, 'generalRemark'),
    });
  }

  const confidence = items.length > 0
    ? Math.min(1, 0.45 + matchCount * 0.06 + Math.min(items.length, 10) * 0.03)
    : matchCount >= 3
      ? 0.35
      : 0;

  return {
    items,
    warnings,
    confidence,
    headerRowIndex,
    columnMap,
    matchCount,
    unsupportedQuotationFields,
  };
}

function mergeHeaderFields(
  target: Record<string, string>,
  targetConfidence: Record<string, number>,
  source: Record<string, string>,
  sourceConfidence: number
): void {
  for (const [key, value] of Object.entries(source)) {
    if (!value?.trim()) continue;
    const prevConf = targetConfidence[key] ?? 0;
    if (!target[key] || sourceConfidence > prevConf) {
      // Do not overwrite a higher-confidence value with a weaker match
      if (target[key] && sourceConfidence < prevConf) continue;
      if (target[key] && sourceConfidence === prevConf) continue;
      target[key] = value;
      targetConfidence[key] = sourceConfidence;
    }
  }
}

export interface FlexibleWorkbookParseResult {
  rawHeader: Record<string, string>;
  items: RfqExcelItem[];
  warnings: string[];
  errors: string[];
  headerLayout: HeaderLayoutKind;
  usedOfficialHeaderSheet: boolean;
  usedOfficialItemsSheet: boolean;
  unmatchedHeaderLabels: string[];
  unsupportedQuotationFields: string[];
}

/**
 * Scan workbook sheets for header fields + item tables without requiring
 * exact official sheet names. Prefer official sheet names when present.
 */
export function parseFlexibleRfqWorkbook(
  workbook: XLSX.WorkBook
): FlexibleWorkbookParseResult {
  const warnings: string[] = [];
  const errors: string[] = [];
  const mergedFields: Record<string, string> = {};
  const fieldConfidence: Record<string, number> = {};
  const unmatchedHeaderLabels: string[] = [];
  let bestHeaderLayout: HeaderLayoutKind = 'none';
  let bestHeaderScore = 0;
  let usedOfficialHeaderSheet = false;
  let usedOfficialItemsSheet = false;

  let bestItems: FlexibleItemsExtraction | null = null;
  let bestItemsSheetName = '';

  for (const sheetName of workbook.SheetNames) {
    if (isSkipSheetName(sheetName)) continue;
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    const officialHeader = isOfficialHeaderSheetName(sheetName);
    const officialItems = isOfficialItemsSheetName(sheetName);

    const header = extractFlexibleHeader(sheet);
    if (header.matchCount > 0) {
      const scoreBoost = officialHeader ? 0.25 : 0;
      const score = header.confidence + scoreBoost;
      mergeHeaderFields(mergedFields, fieldConfidence, header.fields, score);
      unmatchedHeaderLabels.push(...header.unmatchedLabels);
      if (score > bestHeaderScore) {
        bestHeaderScore = score;
        bestHeaderLayout = header.layout;
      }
      if (officialHeader) usedOfficialHeaderSheet = true;
    }

    const items = extractFlexibleItems(sheet);
    if (items.matchCount > 0 && isConfidentItemTable(items.columnMap)) {
      const scoreBoost = officialItems ? 0.25 : 0;
      const score = items.confidence + scoreBoost;
      const bestScore = bestItems
        ? bestItems.confidence + (usedOfficialItemsSheet ? 0.25 : 0)
        : -1;
      if (!bestItems || score > bestScore) {
        bestItems = items;
        bestItemsSheetName = sheetName;
        usedOfficialItemsSheet = officialItems;
      }
    }
  }

  if (Object.keys(mergedFields).length === 0) {
    errors.push(
      `No RFQ header fields recognized. Use "Download Template", or provide Field/Value, horizontal header, or Label: Value layouts. Found sheets: ${workbook.SheetNames.map((n) => `"${n}"`).join(', ')}.`
    );
  }

  if (!bestItems || bestItems.items.length === 0) {
    if (!bestItems || bestItems.matchCount === 0) {
      errors.push(
        `No reliable item table found (need Description + Quantity + UOM columns or aliases). Found sheets: ${workbook.SheetNames.map((n) => `"${n}"`).join(', ')}.`
      );
    } else {
      errors.push(
        `Item table detected on "${bestItemsSheetName}" but no valid item rows found.`
      );
      warnings.push(...bestItems.warnings);
    }
  } else {
    warnings.push(...bestItems.warnings);
  }

  // Sync port/agent input aliases for existing resolver compatibility
  if (mergedFields.portName && !mergedFields.portNameInput) {
    mergedFields.portNameInput = mergedFields.portName;
  }
  if (mergedFields.portAgentName && !mergedFields.portAgentNameInput) {
    mergedFields.portAgentNameInput = mergedFields.portAgentName;
  }

  return {
    rawHeader: mergedFields,
    items: bestItems?.items ?? [],
    warnings,
    errors,
    headerLayout: bestHeaderLayout,
    usedOfficialHeaderSheet,
    usedOfficialItemsSheet,
    unmatchedHeaderLabels: [...new Set(unmatchedHeaderLabels)],
    unsupportedQuotationFields: [...new Set(bestItems?.unsupportedQuotationFields ?? [])],
  };
}

export {
  isSkipSheetName,
  isOfficialHeaderSheetName,
  isOfficialItemsSheetName,
  detectUnsupportedRfqListWorkbook,
};
